import { Text } from "@hackthenorth/north";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import styled from "styled-components";

import { PageWrapper } from "src/shared/components";
import Header from "src/shared/components/Header";
import { EVENT_ID } from "src/shared/constants/event";
import ScheduleToggle from "src/views/schedule/components/ScheduleToggle";
import { SCHEDULE_VIEW_TYPE } from "src/views/schedule/constants";

import { ShiftSearchBar } from "../../shared/components/ShiftSearchBar";
import VolunteerShiftModal from "../../shared/components/VolunteerShiftModal";
import { ScheduleFilterBar } from "../components/ScheduleFilterBar";
import { useGetVolunteerShiftAvailabilityQuery } from "../graphql/getVolunteerShiftAvailability.generated";
import { useGetVolunteerShiftScheduleQuery } from "../graphql/getVolunteerShiftSchedule.generated";
import { TShiftSchedule } from "../types";
import { parseAdminShifts } from "../utils";

import AdminShiftCalendarView from "./AdminShiftCalendarView";
import AdminShiftListView from "./AdminShiftListView";

export const AdminShiftSchedule = () => {
  const [currentView, setCurrentView] = useState<SCHEDULE_VIEW_TYPE>(
    SCHEDULE_VIEW_TYPE.LIST_VIEW
  );

  const [shiftFilters, setShiftFilters] = useState<string[]>([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);

  const [selectedShift, setSelectedShift] = React.useState<number | null>(null);
  const [viewModal, setViewModal] = React.useState<boolean>(false);

  useEffect(() => {
    if (viewModal) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [viewModal]);

  const {
    data: getVolunteerShiftsData,
    loading: fetchingShifts,
    refetch: refetchVolunteerShifts,
  } = useGetVolunteerShiftScheduleQuery({
    variables: { event_id: EVENT_ID },
  });
  const {
    data: getVolunteerShiftAvailabilityData,
    loading: fetchingVolunteerShiftAvailability,
    refetch: refetchShiftAvailability,
  } = useGetVolunteerShiftAvailabilityQuery({
    variables: { event_id: EVENT_ID },
    // Polling remains paused; reload or use action-based refetches.
  });

  const refetchAllShifts = useCallback(() => {
    refetchVolunteerShifts();
    refetchShiftAvailability();
  }, [refetchVolunteerShifts, refetchShiftAvailability]);

  const [adminShifts, setAdminShifts] = useState<TShiftSchedule[]>([]);

  useEffect(() => {
    if (
      getVolunteerShiftsData &&
      !fetchingShifts &&
      getVolunteerShiftAvailabilityData &&
      !fetchingVolunteerShiftAvailability
    ) {
      const newShifts = parseAdminShifts(
        getVolunteerShiftsData,
        getVolunteerShiftAvailabilityData
      );

      setAdminShifts(newShifts);
    }
  }, [
    getVolunteerShiftsData,
    fetchingShifts,
    getVolunteerShiftAvailabilityData,
    fetchingVolunteerShiftAvailability,
  ]);

  const displayedShifts = useMemo(() => {
    if (shiftFilters.length === 0) return adminShifts;
    return adminShifts.filter((shift) => shiftFilters.includes(shift.role));
  }, [adminShifts, shiftFilters]);

  const toggleView = () => {
    setCurrentView((currentView) => {
      if (currentView === SCHEDULE_VIEW_TYPE.LIST_VIEW)
        return SCHEDULE_VIEW_TYPE.CALENDAR_VIEW;

      return SCHEDULE_VIEW_TYPE.LIST_VIEW;
    });
  };

  return (
    <>
      {selectedShift && (
        <VolunteerShiftModal
          shiftId={selectedShift}
          open={viewModal}
          setOpen={setViewModal}
          signupCount={
            adminShifts.find((shift) => shift.shift_id === selectedShift)
              ?.signup_count
          }
          onChange={refetchAllShifts}
        />
      )}
      <Header
        title="Shift Schedule"
        subtitle="Register for your volunteer shifts"
      />

      <PageWrapper>
        <HeaderFlex>
          <Text mods="heading h3">Schedule</Text>
          <ScheduleToggle
            currentView={currentView}
            onToggle={() => toggleView()}
          ></ScheduleToggle>
        </HeaderFlex>
        <div className="text-text-tertiary mb-4">
          All times are in Eastern Time (EDT)
        </div>
        <div style={{ width: "100%" }}>
          {currentView === SCHEDULE_VIEW_TYPE.LIST_VIEW && (
            <ShiftSearchBar
              value={search}
              onChange={(value) => {
                setPage(0);
                setSearch(value);
              }}
            />
          )}
          <ScheduleFilterBar
            shiftFilters={shiftFilters}
            setShiftFilters={setShiftFilters}
            onFiltersChange={() => setPage(0)}
          />

          {currentView === SCHEDULE_VIEW_TYPE.LIST_VIEW && (
            <AdminShiftListView
              fetchingShifts={fetchingShifts}
              adminShifts={displayedShifts}
              search={search}
              page={page}
              onPageChange={setPage}
              setSelectedShift={setSelectedShift}
              setViewModal={setViewModal}
              onRegistrationChange={refetchAllShifts}
            />
          )}
          {currentView === SCHEDULE_VIEW_TYPE.CALENDAR_VIEW && (
            <>
              <AdminShiftCalendarView
                fetchingShifts={fetchingShifts}
                adminShifts={displayedShifts}
                setSelectedShift={setSelectedShift}
                setViewModal={setViewModal}
              />
            </>
          )}
        </div>
      </PageWrapper>
    </>
  );
};

const HeaderFlex = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;
